import { useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { TotpCodeField } from '@/components/TotpCodeField/TotpCodeField'
import styles from './SensitiveSellerIdModal.module.css'

type Props = {
  confirmChange?: boolean
  nextId: string
  isSubmitting: boolean
  errorMessage: string | null
  onCancel: () => void
  onSubmit: (code: string) => void
}

export function SensitiveSellerIdModal({
  confirmChange = false,
  nextId,
  isSubmitting,
  errorMessage,
  onCancel,
  onSubmit,
}: Props) {
  const [stage, setStage] = useState<'confirm' | 'code'>(confirmChange ? 'confirm' : 'code')
  const [code, setCode] = useState('')

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!isSubmitting && /^\d{6}$/.test(code)) onSubmit(code)
  }

  return createPortal(
    <div className={styles.overlay}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="seller-id-modal-title">
        <h2 id="seller-id-modal-title" className={styles.title}>
          {stage === 'confirm' ? 'Confirmar alteração do vendedor' : 'Confirme sua identidade'}
        </h2>
        {stage === 'confirm' ? (
          <>
            <p className={styles.description}>
              O ID do vendedor define quem recebe os pagamentos desta campanha. Deseja realmente
              alterar para <strong>{nextId.trim() || 'nenhum ID'}</strong>?
            </p>
            <div className={styles.actions}>
              <button type="button" className={styles.secondaryButton} onClick={onCancel}>Cancelar</button>
              <button type="button" className={styles.primaryButton} onClick={() => setStage('code')}>
                Sim, continuar
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className={styles.description}>
              Informe um código atual do autenticador para autorizar o ID do vendedor{' '}
              <strong>{nextId.trim() || 'vazio'}</strong>. Se acabou de usar um código para abrir
              esta página, aguarde o próximo.
            </p>
            <TotpCodeField id="seller-id-change-code" value={code} onChange={setCode} disabled={isSubmitting} autoFocus />
            {errorMessage && <p className={styles.error} role="alert">{errorMessage}</p>}
            <div className={styles.actions}>
              <button type="button" className={styles.secondaryButton} onClick={onCancel} disabled={isSubmitting}>Cancelar</button>
              <button type="submit" className={styles.primaryButton} disabled={isSubmitting || code.length !== 6}>
                {isSubmitting ? 'Confirmando...' : 'Confirmar código e salvar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  )
}
